<script setup lang="ts">
import { Head } from "@inertiajs/vue3"
import { SbProgressBar, SbSectionedList } from "@unio/saltbox-ui"

import ContainerRow from "@/components/ContainerRow.vue"
import ListItemRow from "@/components/ListItemRow.vue"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"
import { inventoryPath } from "@/routes"
import type { ContainerSummary, ItemSummary } from "@/types/models"

interface Props {
  container: ContainerSummary
  child_containers: ContainerSummary[]
  child_items: ItemSummary[]
}

defineProps<Props>()

useNavbar({ title: "Inventory Progress" })
</script>

<template>
  <Head :title="`Progress: ${container.name}`" />

  <div class="flex flex-col gap-2">
    <h1 class="text-2xl font-bold">{{ container.name }}</h1>
    <div v-if="container.total_item_count" class="flex items-center gap-4">
      <SbProgressBar
        :current="container.inventoried_count ?? 0"
        :total="container.total_item_count ?? 0"
      />
      <span class="text-muted shrink-0 text-sm">
        {{ container.inventoried_count }} of {{ container.total_item_count }} inventoried
      </span>
    </div>
  </div>

  <SbSectionedList v-if="child_containers.length" label="Containers" bordered separator>
    <ContainerRow
      v-for="child in child_containers"
      :key="child.id"
      :container="child"
      variant="drilled"
      :to="inventoryPath(child.id)"
    />
  </SbSectionedList>

  <SbSectionedList v-if="child_items.length" label="Items" bordered separator>
    <ListItemRow v-for="item in child_items" :key="item.id" :item="item" variant="nested" />
  </SbSectionedList>

  <UEmpty
    v-if="!child_containers.length && !child_items.length"
    :icon="ICON.container"
    title="Empty container"
    description="This container has no items yet."
  />
</template>
